import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { AdminPage, AdminState, Field, Notice, SaveBar, isForbidden, useAdminEditing } from './AdminShell'
import type { Settings } from './api'
import { fetchOrganizations, fetchSettings, updateSettings } from './api'

/**
 * Системные настройки (тикет #97, макет AdmSettings.dc.html).
 *
 * Часовой пояс и напоминание по умолчанию применяются (#110, #111): по поясу повторяются
 * задачи («каждый понедельник в девять», «31-е»), напоминание подставляется в задачи и
 * встречи, где форма не назвала своё. Рабочие часы и дни пока только хранятся — их прочтёт
 * SLA (#115). Это сказано на экране словами — настройка, молча ничего не делающая, находится
 * через полгода тем, кто на неё положился. По той же причине на экране нет секций макета
 * «Лиды и SLA», праздников и ставки НДС: в настройках таких полей нет, и поле, которое
 * ничего не сохраняет, хуже отсутствующего.
 *
 * Сохранение несёт прочитанную версию: правка поверх чужой получает 409 и сообщение, а не
 * тихо отменяет то, что сохранил другой администратор минуту назад.
 */

const ZONES = [
  'Europe/Kaliningrad', 'Europe/Moscow', 'Europe/Samara', 'Asia/Yekaterinburg', 'Asia/Omsk',
  'Asia/Novosibirsk', 'Asia/Krasnoyarsk', 'Asia/Irkutsk', 'Asia/Yakutsk', 'Asia/Vladivostok',
  'Asia/Magadan', 'Asia/Kamchatka',
]
const DAYS = [1, 2, 3, 4, 5, 6, 7]

export function SettingsPage() {
  const { t, user } = useApp()
  const editing = useAdminEditing()
  const queryClient = useQueryClient()
  const settings = useQuery({ queryKey: ['admin-settings'], queryFn: ({ signal }) => fetchSettings(signal) })
  const organizations = useQuery({ queryKey: ['admin-organizations'], queryFn: ({ signal }) => fetchOrganizations(signal) })
  const [draft, setDraft] = useState<Settings | null>(null)

  useEffect(() => {
    if (settings.data) setDraft(settings.data)
  }, [settings.data])

  const save = useMutation({
    mutationFn: (value: Settings) =>
      updateSettings({
        version: value.version,
        timeZone: value.timeZone.trim(),
        workDayStart: value.workDayStart.slice(0, 5),
        workDayEnd: value.workDayEnd.slice(0, 5),
        workDays: value.workDays,
        defaultOrganizationId: value.defaultOrganizationId,
        defaultReminderMinutes: value.defaultReminderMinutes,
      }),
    onSuccess: (saved) => {
      editing.clear()
      queryClient.setQueryData(['admin-settings'], saved)
    },
    onError: editing.fail,
  })

  const stored = settings.data
  const zone = (value: string) => {
    const key = `adm.tz.${value}`
    const label = t(key)
    return label === key ? value : label
  }
  const days = (list: number[]) => list.map((day) => t(`admin.settings.day.${day}`)).join(', ') || '—'
  const orgName = (id: string | null) =>
    id ? (organizations.data?.find((org) => org.id === id)?.name ?? id) : t('adm.settings.noOrganization')

  const changes: string[] = []
  if (stored && draft) {
    const change = (label: string, from: string, to: string) => {
      if (from !== to) changes.push(`${label}: ${from} → ${to}`)
    }
    change(t('admin.settings.timeZone'), zone(stored.timeZone), zone(draft.timeZone))
    change(t('admin.settings.workDayStart'), stored.workDayStart.slice(0, 5), draft.workDayStart.slice(0, 5))
    change(t('admin.settings.workDayEnd'), stored.workDayEnd.slice(0, 5), draft.workDayEnd.slice(0, 5))
    change(t('admin.settings.workDays'), days(stored.workDays), days(draft.workDays))
    change(t('admin.settings.defaultOrganization'), orgName(stored.defaultOrganizationId), orgName(draft.defaultOrganizationId))
    change(t('adm.settings.reminder'), String(stored.defaultReminderMinutes ?? ''), String(draft.defaultReminderMinutes ?? ''))
  }
  // Said under the field and on the bar, not after a round trip: a day ending before it
  // starts would make every working-hours count zero.
  const timeError = draft && draft.workDayEnd.slice(0, 5) <= draft.workDayStart.slice(0, 5)
    ? t('adm.settings.endBeforeStart', { start: draft.workDayStart.slice(0, 5) })
    : null
  const daysError = draft && draft.workDays.length === 0 ? t('adm.settings.noDays') : null
  const reminder = draft?.defaultReminderMinutes
  // Absent (a server before #111) is "unchanged", not invalid; a cleared field is invalid.
  const reminderError = draft && reminder !== undefined
    && (reminder === null || !Number.isInteger(reminder) || reminder < 0 || reminder > 10080)
    ? t('adm.settings.reminder.invalid')
    : null
  const invalid = timeError ?? daysError ?? reminderError

  const toggleDay = (day: number) => {
    if (!draft) return
    const next = draft.workDays.includes(day)
      ? draft.workDays.filter((d) => d !== day)
      : [...draft.workDays, day].sort((a, b) => a - b)
    setDraft({ ...draft, workDays: next })
  }
  const dirty = (from: unknown, to: unknown) => editing.mayEdit && JSON.stringify(from) !== JSON.stringify(to)

  return (
    <AdminPage
      titleKey="nav.adminSettings"
      leadKey="adm.settings.lead"
      readOnly={!editing.mayEdit}
      roKey="adm.settings.ro"
      forbidden={isForbidden(settings.error)}
      headExtra={can(user, 'system.audit', 'read') && (
        <Link to="/admin/audit" className="btn btn-ghost btn-sm"><Icon name="clock" size="sm" />{t('adm.settings.history')}</Link>
      )}
    >
      <AdminState
        isLoading={settings.isLoading}
        error={settings.error}
        onRetry={() => void settings.refetch()}
        forbiddenTitleKey="adm.settings.forbidden"
        errorTitleKey="adm.settings.error"
        skeleton={<SettingsSkeleton />}
      >
        {draft && stored && (
          <>
            <div className="alert alert-info" role="note">
              <Icon name="adm-info" />
              <span>{t('admin.settings.notApplied')}</span>
            </div>
            <form className="panel" style={{ padding: '0 20px' }} aria-label={t('nav.adminSettings')}
                  onSubmit={(event) => {
                    event.preventDefault()
                    if (!invalid) save.mutate(draft)
                  }}>
              <section className="settings-sec" aria-labelledby="sec-cal">
                <div>
                  <h2 className="sec-t" id="sec-cal">{t('adm.settings.calendar')}</h2>
                  <p className="sec-d">{t('adm.settings.calendar.text')}</p>
                </div>
                <div className="sec-fields">
                  <div className={dirty(stored.workDays, draft.workDays) ? 'field is-dirty' : 'field'} style={{ gridColumn: 'span 2' }}>
                    <span className="label" id="f-days-l">{t('admin.settings.workDays')}</span>
                    {editing.mayEdit ? (
                      <div className="seg" role="group" aria-labelledby="f-days-l" style={{ width: 'fit-content' }}>
                        {DAYS.map((day) => (
                          <button key={day} type="button" aria-pressed={draft.workDays.includes(day)}
                                  style={{ minWidth: 38, justifyContent: 'center' }} onClick={() => toggleDay(day)}>
                            {t(`admin.settings.day.${day}`)}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <span className="t-md adm-ro-value">{days(draft.workDays)}</span>
                    )}
                    {daysError ? (
                      <span className="err-text"><Icon name="adm-error" size="sm" />{daysError}</span>
                    ) : (
                      <span className="help">{t('adm.settings.days.help')}</span>
                    )}
                  </div>
                  <Field label={t('admin.settings.workDayStart')} htmlFor="f-from"
                         dirty={dirty(stored.workDayStart.slice(0, 5), draft.workDayStart.slice(0, 5))}>
                    {editing.mayEdit ? (
                      <input id="f-from" type="time" className="input" value={draft.workDayStart.slice(0, 5)}
                             onChange={(e) => setDraft({ ...draft, workDayStart: e.target.value })} />
                    ) : (
                      <span className="t-md adm-ro-value">{draft.workDayStart.slice(0, 5)}</span>
                    )}
                  </Field>
                  <Field label={t('admin.settings.workDayEnd')} htmlFor="f-to" error={editing.mayEdit ? timeError : null}
                         dirty={dirty(stored.workDayEnd.slice(0, 5), draft.workDayEnd.slice(0, 5))}>
                    {editing.mayEdit ? (
                      <input id="f-to" type="time" className={timeError ? 'input is-invalid' : 'input'} aria-invalid={Boolean(timeError)}
                             value={draft.workDayEnd.slice(0, 5)} onChange={(e) => setDraft({ ...draft, workDayEnd: e.target.value })} />
                    ) : (
                      <span className="t-md adm-ro-value">{draft.workDayEnd.slice(0, 5)}</span>
                    )}
                  </Field>
                  <Field label={t('admin.settings.timeZone')} htmlFor="f-tz" help={t('adm.settings.tz.help')}
                         dirty={dirty(stored.timeZone, draft.timeZone)}>
                    {editing.mayEdit ? (
                      <select id="f-tz" className="input" value={draft.timeZone} onChange={(e) => setDraft({ ...draft, timeZone: e.target.value })}>
                        {(ZONES.includes(draft.timeZone) ? ZONES : [draft.timeZone, ...ZONES]).map((value) => (
                          <option key={value} value={value}>{zone(value)}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="t-md adm-ro-value">{zone(draft.timeZone)}</span>
                    )}
                  </Field>
                </div>
              </section>
              <section className="settings-sec" aria-labelledby="sec-def">
                <div>
                  <h2 className="sec-t" id="sec-def">{t('adm.settings.defaults')}</h2>
                  <p className="sec-d">{t('adm.settings.defaults.text')}</p>
                </div>
                <div className="sec-fields">
                  <Field label={t('admin.settings.defaultOrganization')} htmlFor="f-org" help={t('adm.settings.org.help')}
                         dirty={dirty(stored.defaultOrganizationId, draft.defaultOrganizationId)}>
                    {editing.mayEdit ? (
                      <select id="f-org" className="input" value={draft.defaultOrganizationId ?? ''}
                              onChange={(e) => setDraft({ ...draft, defaultOrganizationId: e.target.value || null })}>
                        <option value="">{t('adm.settings.noOrganization')}</option>
                        {(organizations.data ?? []).map((org) => (
                          <option key={org.id} value={org.id}>{org.name}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="t-md adm-ro-value">{orgName(draft.defaultOrganizationId)}</span>
                    )}
                  </Field>
                </div>
              </section>
              <section className="settings-sec" aria-labelledby="sec-rem">
                <div>
                  <h2 className="sec-t" id="sec-rem">{t('adm.settings.reminders')}</h2>
                  <p className="sec-d">{t('adm.settings.reminders.text')}</p>
                </div>
                <div className="sec-fields">
                  <Field label={t('adm.settings.reminder')} htmlFor="f-rem" help={t('adm.settings.reminder.help')}
                         error={editing.mayEdit ? reminderError : null}
                         dirty={dirty(stored.defaultReminderMinutes, draft.defaultReminderMinutes)}>
                    {editing.mayEdit ? (
                      <input id="f-rem" className="input" type="number" min={0} max={10080} step={5}
                             value={draft.defaultReminderMinutes ?? ''}
                             onChange={(e) => setDraft({ ...draft, defaultReminderMinutes: e.target.value === '' ? null : Number(e.target.value) })} />
                    ) : (
                      <span className="t-md adm-ro-value">{t('cal.remind.before', { minutes: draft.defaultReminderMinutes ?? 0 })}</span>
                    )}
                  </Field>
                </div>
              </section>
            </form>
            <Notice text={editing.notice} />
            {editing.mayEdit && (
              <SaveBar
                changes={changes}
                invalid={invalid}
                pending={save.isPending}
                onReset={() => {
                  editing.clear()
                  setDraft(stored)
                }}
                onSave={() => save.mutate(draft)}
              />
            )}
          </>
        )}
      </AdminState>
    </AdminPage>
  )
}

function SettingsSkeleton() {
  const { t } = useApp()
  return (
    <div className="panel" style={{ padding: '0 20px' }} aria-busy="true" aria-label={t('app.loading')}>
      {[140, 100].map((width) => (
        <div className="settings-sec" key={width}>
          <div className="stack" style={{ gap: 8 }}>
            <span className="skel" style={{ width: 140, height: 14 }} />
            <span className="skel" style={{ width: 220, height: 8 }} />
          </div>
          <div className="sec-fields">
            <div className="stack" style={{ gap: 6 }}><span className="skel" style={{ width: 120, height: 8 }} /><span className="skel" style={{ height: 32, borderRadius: 6 }} /></div>
            <div className="stack" style={{ gap: 6 }}><span className="skel" style={{ width, height: 8 }} /><span className="skel" style={{ height: 32, borderRadius: 6 }} /></div>
          </div>
        </div>
      ))}
    </div>
  )
}
